/* jshint ignore:start */

'use strict';

export function Validator(
	$i18next,
	options = {
		// for both image and video:
		mediaType: 'image',
		fileType: '',
		aspect: { min: 0, max: 0 },
		maxWidth: 0,
		minWidth: 0,
		minHeight: 0,
		maxFileSize: 0,
		minFileSize: 0,

		// only for video:
		maxDuration: 0,
		minDuration: 0,
		maxVideoBitrate: 0,
		maxAudioBitrate: 0,
		videoCodecNames: [],
		audioCodecNames: [],
		maxAudioSampleRate: 0,
		frameRate: {
			max: 0,
			min: 0
		},
		noEditList: false
	}
) {
	const validateImage = (
		src = {
			imgData: null,
			contentType: '',
			size: 0,
			metaData: {
				width: 0,
				height: 0
			}
		}
	) => {
		// pass this to react component
		src.error = '';
		src.userAction = false;
		let width;
		let height;
		let filesize;

		const mediaTypeCapital = $i18next.t('general.image_plain');

		if (src.imgData) {
			// means it's onModidy
			({ height, width } = src.imgData);
		} else {
			const meta = src.metaData || src.meta;
			if (!meta) {
				src.error = $i18next.t('posts.draft.media_meta_missing');
				src.isValid = false;
				return false;
			}
			({ width, height } = meta);
			filesize = src.size || src.fileSize;
		}

		if (options.aspect && options.aspect.min && options.aspect.max) {
			const fileaspect = width / height;
			let validAspect = options.aspect.min <= fileaspect && fileaspect <= options.aspect.max;
			if (!validAspect) {
				src.userAction = true;
				src.error = $i18next.t('general.media_upload.wrong_aspect_ratio');
			}
		}

		if (options.maxWidth && width > options.maxWidth) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.too_big_width', {
				mediaType: mediaTypeCapital
			});
		}

		if (width < options.minWidth || height < options.minHeight) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.too_small', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.maxFileSize && filesize && filesize > options.maxFileSize) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.file_too_big', {
				mediaType: mediaTypeCapital
			});
		}

		if (
			options.mediaType === 'image' &&
			src.contentType &&
			!src.contentType.toLowerCase().startsWith('image/')
		) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.filetype_not_valid', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.minFileSize && filesize && filesize < options.minFileSize) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.file_too_small', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.fileType === 'jpg' && src.contentType !== 'image/jpeg') {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.filetype_not_valid', {
				mediaType: mediaTypeCapital
			});
		}

		if (
			options.fileType === 'jpg,png' &&
			src.contentType !== 'image/jpeg' &&
			src.contentType !== 'image/png'
		) {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.filetype_not_valid', {
				mediaType: mediaTypeCapital
			});
		}

		if (src.contentType && src.contentType.toLowerCase() === 'image/gif') {
			src.userAction = false;
			src.error = $i18next.t('general.media_upload.filetype_not_valid', {
				mediaType: mediaTypeCapital
			});
		}
		src.isValid = !src.error;
		return !src.error;
	};
	const validateVideo = (
		file = {
			contentType: '',
			size: 0,
			metaData: {
				width: 0,
				height: 0,
				duration: 0,
				video_bitrate: 0,
				audio_bitrate: 0,
				video_codec_name: '',
				audio_codec_name: '',
				audio_sample_rate: 0,
				avg_frame_rate: 0,
				hasEditList: false
			}
		}
	) => {
		const metaData = file.metaData || file.meta;
		if (!metaData) {
			// old imported posts are missing metaData
			file.error = $i18next.t('posts.draft.media_meta_missing');
			file.isValid = false;
			return false;
		}
		const { width, height, duration, video_bitrate, audio_bitrate } = metaData;
		const filesize = file.size || file.fileSize;

		const mediaTypeCapital = $i18next.t('general.video_plain');

		if (options.maxVideoBitrate && video_bitrate > options.maxVideoBitrate) {
			file.error = $i18next.t('general.media_upload.video_bitrate_too_high');
		}

		if (options.maxAudioBitrate && audio_bitrate > options.maxAudioBitrate) {
			file.error = $i18next.t('general.media_upload.audio_bitrate_too_high');
		}

		if (options.maxAudioSampleRate && metaData.audio_sample_rate) {
			if (metaData.audio_sample_rate > options.maxAudioSampleRate) {
				file.error = $i18next.t('general.media_upload.audio_sample_rate_too_high');
			}
		}

		if (options.frameRate?.max || options.frameRate?.min) {
			if (
				!metaData.avg_frame_rate ||
				metaData.avg_frame_rate > options.frameRate.max ||
				metaData.avg_frame_rate < options.frameRate.min
			) {
				file.error = $i18next.t('general.media_upload.frame_rate_wrong', {
					frameRate: `${options.frameRate.min}-${options.frameRate.max}`
				});
			}
		}

		if (options.aspect && options.aspect.min && options.aspect.max) {
			const fileaspect = width / height;
			let validAspect = options.aspect.min <= fileaspect && fileaspect <= options.aspect.max;
			if (!validAspect) {
				file.error = $i18next.t('general.media_upload.wrong_aspect_ratio');
			}
		}

		if (options.maxWidth && width > options.maxWidth) {
			file.error = $i18next.t('general.media_upload.too_big_width', {
				mediaType: mediaTypeCapital
			});
		}

		if (width < options.minWidth || height < options.minHeight) {
			file.error = $i18next.t('general.media_upload.too_small', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.maxFileSize && filesize > options.maxFileSize) {
			file.error = $i18next.t('general.media_upload.file_too_big', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.minFileSize && filesize > options.minFileSize) {
			file.error = $i18next.t('general.media_upload.file_too_small', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.minDuration && duration < options.minDuration) {
			file.error = $i18next.t('general.media_upload.video_too_short');
		}

		if (options.maxDuration && duration > options.maxDuration) {
			file.error = $i18next.t('general.media_upload.video_too_long');
		}

		if (
			options.mediaType === 'video' &&
			file.contentType &&
			!file.contentType.toLowerCase().startsWith('video/')
		) {
			file.error = $i18next.t('general.media_upload.filetype_not_valid', {
				mediaType: mediaTypeCapital
			});
		}

		if (options.videoCodecNames?.length) {
			if (!options.videoCodecNames.includes(metaData.video_codec_name)) {
				file.error = $i18next.t('general.media_upload.video_codec_wrong', {
					codecs: options.videoCodecNames.filter(r => r).join(', ')
				});
			}
		}

		if (options.audioCodecNames?.length) {
			if (!options.audioCodecNames.includes(metaData.audio_codec_name)) {
				file.error = $i18next.t('general.media_upload.audio_codec_wrong', {
					codecs: options.audioCodecNames.filter(r => r).join(', ')
				});
			}
		}

		if (options.noEditList) {
			if (metaData.hasEditList) {
				file.error = $i18next.t('general.media_upload.has_edit_list');
			}
		}

		file.isValid = !file.error;
		return !file.error;
	};

	return { validateImage, validateVideo };
}

if (window.angular) {
	window.angular.module('PinMeTo.services').service('pmtMediaService', [
		'$i18next',
		function ($i18next) {
			this.Validator = Validator.bind(this, $i18next);
		}
	]);
}

/* jshint ignore:end */
